import * as t from 'io-ts';

import { createFormData } from '@/shared/api/create-form-data';
import { get, post, put, del } from '@/shared/api/fetch-based-http-methods';
import { prepareDate, prepareDateForRequest } from '@/shared/api/prepare-date';
import { EntityPage } from '@/shared/models/entity-page';
import { UploadResult } from '@/shared/models/upload-result';
import { prepareUploadedFile, UploadedFile } from '@/shared/models/uploaded-file';
import { IDocUploaderForm } from '@/shared/store/doc-uploader/types';
import { formatDate } from '@/shared/utils/formatDate';
import { getApiUrl } from '@/shared/utils/getApiUrl';
import { getOnlineStatus } from '@/shared/utils/getOnlineStatus';
import { skipNullishDeep } from '@/shared/utils/skip-nullish-deep';
import { w4dCarsRemoteAPI } from '@src-common/api/definitions/w4d-cars-endpoints';
import type { TCarFormResponse } from '@src-common/api/definitions/w4d-cars-types';
import {
  CarDetails as RawCarDetails,
  CarBrief as RawCarBrief,
} from '@src-common/api/definitions/w4d-cars-types';
import { createClient } from '@src-common/api/src/create-client';

import { CarValidation } from '../model';
import type { CarModels, CarFull, Car, CarDocType } from '../model';
import { CarDocDefinition } from '../model/car-doc-type';
import { VehicleCreateForm, VehicleUpdateForm } from '../model/car-form';

import { EngineType, getHardcodedCarModels } from './hardcoded-make-models';

type CarUpdateResult = [CarFull, undefined] | [undefined, CarValidation];

const client = createClient(w4dCarsRemoteAPI({ apiHost: getApiUrl(), get, post, put, del }));

const prepareCarRequest = (car: VehicleCreateForm) => ({
  make: car.make,
  model: car.model,
  generation: car.generation ?? null,
  license_plate: car.licensePlate,
  license_number: car.licenseNumber || null,
  license_authority: car.licensingAuthority,
  color: car.color,
  interior_color: car.interiorColor,
  year: Number(car.year),
  first_sale_date: prepareDateForRequest(car.registrationDate),
  speed: null,
  pin: null,
  access_status: null,
  tags: [],
  current_driver_id: null,
  current_order_id: null,
  wheelbase: car.wheelbase,
  vin: car.vin,
  engine_type: car.engineType,
});

const prepareCar = (raw: t.TypeOf<typeof RawCarBrief>): Car => ({
  accessStatus: raw.access_status,
  color: raw.color,
  id: raw.id,
  interiorColor: raw.interior_color ?? '',
  licensePlate: raw.license_plate,
  make: raw.make,
  model: raw.model,
  generation: raw.generation ?? '',
  year: String(raw.year),
  wheelbase: raw.wheelbase || '',
  docUploads: !raw.uploads
    ? {}
    : Object.fromEntries<UploadedFile<CarDocType>>(
        raw.uploads.map(rawUpload => [rawUpload.type, prepareUploadedFile<CarDocType>(rawUpload)]),
      ),
  tags: raw.tags,
  vin: raw.vin ?? '',
  onlineStatus: getOnlineStatus(raw.status_hash),
  engineType: raw.engine_type as EngineType,
  currentChauffeur: raw.current_driver
    ? {
        id: raw.current_driver.id,
        firstName: raw.current_driver.first_name ?? '',
        lastName: raw.current_driver.last_name ?? '',
      }
    : null,
});

const prepareCarDetails = (raw: t.TypeOf<typeof RawCarDetails>): CarFull => ({
  licenseNumber: raw.license_number || '',
  licensingAuthority: raw.license_authority,
  registrationDate: prepareDate(raw.first_sale_date),
  ...prepareCar(raw),
});

const prepareFormResponse = (resp: TCarFormResponse): CarUpdateResult => {
  if ('errors' in resp) {
    return [
      undefined,
      {
        licensePlate: resp.errors?.license_plate,
        licenseNumber: resp.errors?.license_number,
        licensingAuthority: resp.errors?.license_authority,
        color: resp.errors?.color,
        interiorColor: resp.errors?.interior_color,
        make: resp.errors?.make,
        model: resp.errors?.model,
      },
    ];
  }

  return [prepareCarDetails(resp.car), undefined];
};

const carAPI = {
  createCar: async (car: VehicleCreateForm): Promise<CarUpdateResult> => {
    const resp = await client.createCar({ payload: prepareCarRequest(car) });

    return prepareFormResponse(resp);
  },
  updateCar: async (car: VehicleUpdateForm): Promise<CarUpdateResult> => {
    const resp = await client.updateCar({
      payload: prepareCarRequest(car),
      pathParams: { carId: String(car.id) },
    });

    return prepareFormResponse(resp);
  },
  deleteCar: async (carId: string) =>
    client.deleteCar({
      pathParams: { carId },
    }),
  fetchAllModels: async (cityId: string, cityName: string): Promise<CarModels[]> => {
    //const response = await client.readAllModels({ pathParams: { city_id: cityId } });
    const response = { results: getHardcodedCarModels(cityName) };

    return response.results.map(raw => ({
      id: raw._id,
      make: raw.make,
      models: raw.modifications,
    }));
  },
  fetchCars: async ({
    limit = 250,
    offset = 0,
  }: {
    limit?: number;
    offset?: number;
  } = {}): Promise<EntityPage<Car>> => {
    const response = await client.readCars({ pathParams: { limit, offset } });
    const items = response.results.map(prepareCar);

    return {
      items,
      total: response.total_entries,
      limit: response.limit,
      offset: response.skip,
    };
  },
  fetchCarById: async (id: string): Promise<CarFull> => {
    const response = await client.readCarDetails({ pathParams: { id } });
    const raw = response.car;

    return prepareCarDetails(raw);
  },
  fetchDocDefinitions: async (): Promise<Array<CarDocDefinition>> => {
    const response = await client.readCarDocumentDefinitions();

    return response.map(raw => ({
      docType: raw.type,
      title: raw.caption,
      comment: raw.comment || undefined,
      desc: raw.description || undefined,
      exampleUrl: raw.example_url || undefined,
      isOptional: raw.optional,
      isExpires: raw.expires || false,
    }));
  },
  uploadFile: async (
    form: IDocUploaderForm<CarDocType>,
    carId: string,
  ): Promise<UploadResult<CarDocType>> => {
    const response = await client.uploadCarDocument({
      pathParams: { carId },
      payload: createFormData(
        skipNullishDeep({
          type: form.docType,
          expires_at: form.expiresAt && formatDate(form.expiresAt), // YYYY-MM-DD
          upload_file: form.file?.file,
        }),
      ),
    });

    if (!('upload' in response)) {
      if ('errors' in response) {
        if (response.errors.file) {
          return {
            error: response.errors.file[0],
          };
        }
      }
    }

    if ('upload' in response) {
      return {
        id: response.upload.id,
        type: response.upload.type,
      };
    }

    return {
      error: '',
    };
  },
};

export { carAPI, getOnlineStatus };
